import { Node, PasteRule } from '@tiptap/core';
import { InputRule } from '@tiptap/react';

export interface ScriptFieldsOptions {
    handleAddScriptField: (scriptField: string) => void;
    isValidScriptField: (scriptField: string) => boolean;
    onInvalidScriptField: () => void;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        scriptField: {
            injectScriptField: (field: string) => ReturnType;
            removeScriptField: (field: string) => ReturnType;
        };
    }
}

export const inputRegex = /{{(.*?)}}/;

/**
 * This extension is used to add script fields to the editor.
 */
export const ScriptFieldExtension = Node.create<ScriptFieldsOptions>({
    name: 'scriptField',
    group: 'inline',
    inline: true,
    code: true,
    atom: true,

    addOptions() {
        return {
            handleAddScriptField: () => {}, // Default handler function
            isValidScriptField: () => true,
            onInvalidScriptField: () => {},
        };
    },
    addCommands() {
        return {
            injectScriptField:
                field =>
                ({ chain }) => {
                    return chain()
                        .insertContent({ type: 'scriptField', attrs: { 'data-script-field': field } })
                        .insertContent(' ')
                        .focus()
                        .run();
                },
            removeScriptField:
                field =>
                ({ tr, state }) => {
                    const positionsToDelete: { start: number; end: number }[] = [];

                    // Collect positions of nodes to delete
                    state.doc.descendants((node, pos) => {
                        if (node.type.name === 'scriptField' && node.attrs['data-script-field'] === field) {
                            positionsToDelete.push({ start: pos, end: pos + node.nodeSize });
                        }

                        return true;
                    });

                    // chain all the delete commands
                    positionsToDelete.reverse().forEach(({ start, end }) => {
                        tr.delete(start, end);
                    });

                    return true;
                },
        };
    },

    addAttributes() {
        return {
            'data-script-field': {
                default: null,
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'code[data-script-field]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['code', HTMLAttributes, HTMLAttributes['data-script-field']];
    },

    addInputRules() {
        const inputRule = new InputRule({
            find: inputRegex,
            handler: ({ state: { tr }, range, match }) => {
                const content = match[match.length - 1];
                if (!content) {
                    return null;
                }

                const isValid = this.options.isValidScriptField(content);

                if (!isValid) {
                    this.options.onInvalidScriptField();
                    return null;
                }

                // If the match ends with a space, we need to adjust the range. Otherwise, the space removed instead of the first '{' character.
                if (match.input?.endsWith(' ')) {
                    range.from -= 1;
                }

                this.options.handleAddScriptField(content); // Call the handler function with content

                tr.replaceWith(range.from, range.to, this.type.create({ 'data-script-field': content }));
            },
        });

        return [inputRule];
    },

    addPasteRules() {
        const pasteRule = new PasteRule({
            find: inputRegex,
            handler: ({ state: { tr }, range, match }) => {
                const content = match[match.length - 1];

                if (!content) {
                    return null;
                }

                const isValid = this.options.isValidScriptField(content);

                if (!isValid) {
                    this.options.onInvalidScriptField();
                    return null;
                }

                this.options.handleAddScriptField(content); // Call the handler function with content

                tr.replaceWith(range.from, range.to, this.type.create({ 'data-script-field': content }));
            },
        });

        return [pasteRule];
    },

    addKeyboardShortcuts() {
        return {
            // Delete the script field when backspace is pressed
            Backspace: ({ editor }) => {
                const { state, view } = editor;
                const { selection } = state;
                const { $from } = selection;
                const node = $from.nodeBefore;

                if (node && node.type.name === 'scriptField') {
                    const tr = state.tr.delete($from.pos - node.nodeSize, $from.pos);
                    view.dispatch(tr);
                    return true;
                }

                return false;
            },
        };
    },
});
